import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@syncanix/ui';
import { Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchResultsList, searchOptionId } from '@/components/SettingsSearch';
import { useT } from '@/core/providers/I18nProvider';
import { matchesHaystack, normalizeSearch } from '@/lib/search-haystack';

import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { ReactElement, ReactNode } from 'react';

/**
 * ⌘K command palette (blueprint Phase 10 — the flagship reach surface). A
 * keyboard-first launcher over the app's NAVIGATION + a few global ACTIONS: open
 * with ⌘K / Ctrl+K (wired in the AppShell), type to filter (label or group), ↑/↓
 * to move, Enter/click to run. Nav is fed the SAME localized, Labs-flag-filtered
 * `sidebarItems` the sidebar renders, so it can never offer a destination the
 * operator can't actually see (e.g. a gated hub while its flag is off).
 *
 * Built on the canonical `Dialog` (focus trap + scroll lock + Esc + portal) with a
 * lightweight combobox pattern — no new dependency for a filtered list.
 */

/** A non-navigation command (toggle theme, sign out, …) the palette can run. */
export interface PaletteAction {
  readonly id: string;
  readonly label: string;
  readonly run: () => void;
  readonly group?: string;
  /** Localized "Page › Tab › Card" trail, shown at the end of the row. */
  readonly trail?: readonly string[];
  /**
   * Pre-normalized searchable text. When a source has one (the settings index
   * builds it per locale), the palette matches on it rather than on the label —
   * which is how typing "delete after days" finds "Conversation retention".
   */
  readonly haystack?: string;
  readonly icon?: ReactNode;
}

/** The unified, flattened command the list renders — nav or action. */
interface Command {
  readonly key: string;
  readonly label: string;
  readonly trail: readonly string[] | undefined;
  readonly icon: ReactNode;
  readonly haystack: string;
  readonly perform: () => void;
}

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly items: readonly SidebarItem[];
  /** Global actions surfaced above the nav destinations. Optional. */
  readonly actions?: readonly PaletteAction[];
}

export function CommandPalette({
  open,
  onOpenChange,
  items,
  actions = [],
}: CommandPaletteProps): ReactElement {
  const { t } = useT();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const close = (): void => {
    onOpenChange(false);
  };

  // Flatten actions + nav into one command list (actions first — they're the
  // "do something now" commands; nav destinations follow).
  const commands = useMemo<Command[]>(() => {
    const actionCommands: Command[] = actions.map((action) => ({
      key: `action:${action.id}`,
      label: action.label,
      trail: action.trail ?? (action.group === undefined ? undefined : [action.group]),
      icon: action.icon,
      haystack: action.haystack ?? normalizeSearch([action.label, action.group ?? ''].join(' ')),
      perform: () => {
        close();
        action.run();
      },
    }));
    const navCommands: Command[] = items.map((item) => ({
      key: `nav:${item.to}`,
      label: item.label,
      trail: item.group === undefined ? undefined : [item.group],
      icon: item.icon,
      haystack: normalizeSearch([item.label, item.group ?? ''].join(' ')),
      perform: () => {
        close();
        void navigate(item.to);
      },
    }));
    return [...actionCommands, ...navCommands];
    // `close`/`navigate` are stable enough for the palette's lifetime; deps kept
    // to the data that actually changes the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, actions]);

  const results = useMemo(
    // The SAME matcher the top-bar field runs, over the same haystacks: every
    // term must appear, diacritics folded. An empty query matches everything,
    // which is right here — the palette's empty state IS the launcher.
    () => commands.filter((command) => matchesHaystack(command.haystack, query)),
    [commands, query],
  );

  // Reset the query + highlight each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
    }
  }, [open]);

  // Keep the highlight in range as the result set shrinks while typing.
  useEffect(() => {
    setHighlight((h) => (results.length === 0 ? 0 : Math.min(h, results.length - 1)));
  }, [results.length]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h + 1) % results.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h - 1 + results.length) % results.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      results[highlight]?.perform();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Anchored 20% down rather than centered, so the Dialog's own
        // `100dvh - 2rem` clamp (which assumes centering) would still let a
        // short viewport push the results list off the bottom. Re-clamp to the
        // space that actually remains below the anchor.
        className="top-[20%] max-h-[calc(80dvh-2rem)] max-w-lg translate-y-0 p-0"
        onOpenAutoFocus={(e) => {
          // Focus our search input, not the Dialog's default close button.
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Radix requires a title on every DialogContent, and it is right to: a
            dialog that opens with no accessible name announces as an unlabelled
            group, so a screen-reader user is dropped into a modal with no idea
            what it is. This one had none — the console said so on every ⌘K, and
            it was found by pressing the key, not by a test. Visually hidden
            because the search field is self-evidently the palette on screen. */}
        <DialogTitle className="sr-only">{t('palette.searchAria')}</DialogTitle>
        {/* The description half of the same rule, and it was found the same way
            — by the console gate rather than by an assertion. Radix warns for
            it because a NAME says which dialog opened while a DESCRIPTION says
            how to work it, and a palette is exactly the surface where that
            matters: the whole interaction is keyboard-only, and a screen-reader
            user who lands here with only "Search or type a command" has not
            been told that the arrow keys move and Enter opens. Visually hidden,
            because on screen the list and its highlight say it. */}
        <DialogDescription className="sr-only">{t('palette.dialogDescription')}</DialogDescription>
        <div className="flex items-center gap-2 border-b border-hairline px-3">
          <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            // Names the highlighted row, so a screen reader announces the
            // result the arrow keys moved to. Without it the listbox is
            // navigable and silent.
            {...(results[highlight] === undefined
              ? {}
              : { 'aria-activedescendant': searchOptionId(listId, results[highlight].key) })}
            aria-label={t('palette.searchAria')}
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            className="h-11 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
          />
        </div>

        <SearchResultsList
          id={listId}
          ariaLabel={t('palette.resultsAria')}
          rows={results.map((command) => ({
            key: command.key,
            label: command.label,
            trail: command.trail,
            icon: command.icon,
            onSelect: command.perform,
          }))}
          highlight={highlight}
          onHighlight={setHighlight}
          emptyLabel={t('palette.empty')}
          className="max-h-80 overflow-y-auto"
        />
      </DialogContent>
    </Dialog>
  );
}
